// src/utils/activityLogger.js - CORRECTED
import enhancedUserService from '../services/enhancedUserService';
import { db, auth, functions } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import notificationService from '../services/notificationService';
import { buildWritingDataFromExam } from './writingDataFromExam';
import { trackFirstStudyAction } from './conversionEvents';
import subscriptionService from '../services/subscriptionService';
import freeAttemptService from '../services/freeAttemptService';

export const initializeUserData = async (user, signupData) => {
  try {
    console.log('Initializing user data for:', user.email);
    const result = await enhancedUserService.createEnhancedUserProfile(user, {
      firstName: signupData.firstName,
      lastName: signupData.lastName,
      selectedExamProvider: signupData.selectedExamProvider,
      selectedExamLevel: signupData.selectedExamLevel
    });
    console.log('User data initialized:', result);
    return result;
  } catch (error) {
    console.error('Error initializing user data:', error);
    return { success: false, error: error.message };
  }
};
export const logUserActivity = async (activity) => {
  const user = auth.currentUser;
  if (!user) return;

  try {
    // Write to users/{userId}/activityLogs subcollection for proper permissions
    await addDoc(collection(db, 'users', user.uid, 'activityLogs'), {
      userId: user.uid,
      userEmail: user.email,
      action: activity.action,
      details: activity.details || {},
      timestamp: serverTimestamp(),
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      },
      sessionId: activity.sessionId || null
    });

    // Update streak for any meaningful activity (unless explicitly disabled)
    // This ensures that ANY activity on the website counts toward the streak
    if (activity.updateStreak !== false) {
      try {
        await enhancedUserService.updateStudyStreak(user.uid);
      } catch (streakError) {
        // Don't fail the activity log if streak update fails
        console.warn('Failed to update streak for activity:', streakError);
      }
    }
  } catch (error) {
    console.error('Error logging activity:', error);
  }
};
// For your exam pages - call this when exam is completed
export const logExamCompletion = async (examData, results, feedbackData = null) => {
  const user = auth.currentUser;
  if (!user) {
    console.error("Cannot log exam completion, no user is signed in.");
    return null;
  }
  const userId = user.uid;
  trackFirstStudyAction({ userId, actionType: 'exam', activityId: examData.examId });

  // Use the exam service's maxScore when present (e.g. TELC B2 = 225 written, not 300 total)
  const maxScore = results.maxScore != null && results.maxScore > 0
    ? results.maxScore
    : examData.sections.reduce((total, section) => total + (section.totalPoints || 0), 0);

  // Safety: always persist the user's writing texts with the result, even if no correction runs
  const writingData = buildWritingDataFromExam(examData, results.userAnswers || {});
  if (writingData) {
    console.log('Logging exam completion: including writing texts for', Object.keys(writingData.texts).length, 'task(s)');
  }

  try {
    console.log('Logging exam completion:', examData.examId);

    // Skip a finished attempt that is already stored. This check has existed in
    // enhancedUserService for a long time but was never called from anywhere,
    // which is why some accounts show the same score saved several times a few
    // minutes apart — and why admin "exams completed" counts overstate the
    // number of real sittings. Runs before trackStudySession so a replay does
    // not duplicate the study session either.
    const duplicateId = await enhancedUserService.findRecentDuplicateResult(
      userId,
      examData.examId,
      results.timeSpent || 0
    );
    if (duplicateId) {
      console.log('Identical result already saved in the last 5 minutes; reusing', duplicateId);
      return duplicateId;
    }

    // Track the study session
    await enhancedUserService.trackStudySession({
      type: 'exam',
      activityId: examData.examId,
      title: `${examData.examProvider} ${examData.level} Exam`,
      duration: results.timeSpent || 0,
      score: results.overallScore || 0,
      maxScore,
      completed: true,
      correctAnswers: results.correctAnswers || 0,
      totalAnswers: results.totalQuestions || 0,
      examProvider: examData.examProvider,
      examLevel: examData.level,
      sectionScores: results.sectionScores || {}
    });

    // Save detailed exam results (including writingData so user's text is always stored)
    const saveResult = await enhancedUserService.saveExamResults(
      userId,
      examData.examId,
      {
        examId: examData.examId,
        provider: examData.examProvider,
        level: examData.level,
        variantName: examData.variantName,
        examFileName: examData.examFileName,
        // 'paper' for the booklet view, 'digital' for the simulator.
        examMode: results.examMode === 'paper' ? 'paper' : 'digital',
        overallScore: results.overallScore || 0,
        maxScore,
        sectionScores: results.sectionScores || {},
        userAnswers: results.userAnswers || {},
        correctAnswers: results.correctAnswers || {},
        flaggedQuestions: results.flaggedQuestions || [],
        timeSpent: results.timeSpent || 0,
        sectionTimes: results.sectionTimes || {},
        totalQuestions: results.totalQuestions || 0,
        writingData: writingData || null
      },
      feedbackData
    );
    if (saveResult.success) {
      console.log('Exam completion logged successfully with ID:', saveResult.id);

      // Free tier: a COMPLETED exam consumes the single free attempt. Written
      // only after the result is safely stored, so a failed save never costs
      // the user their one go.
      //
      // Deliberately gated on isFree: a premium account must NEVER carry this
      // flag. firestore.rules allows an examResults create whenever the flag is
      // absent and does not consult subscription state (it cannot — the honest
      // record of a purchase is the subscriptions/ collection, which rules
      // cannot query), so flag-absence is exactly what keeps paying customers
      // able to save. Writing it for a subscriber would lock them out the
      // moment their plan lapsed.
      try {
        const status = await subscriptionService.getSubscriptionStatus(userId);
        if (status?.isFree && !status?.isAdmin) {
          await freeAttemptService.markExamAttempt(userId);
        }
      } catch (flagErr) {
        // Non-fatal: the result is already saved. Worst case the user gets one
        // extra exam, which is far better than losing a finished one.
        console.warn('Could not record free exam attempt:', flagErr);
      }

      // Fire-and-forget: try to send first-exam congratulations email (no-op if already sent)
      try {
        const sendFirstExamEmail = httpsCallable(functions, 'sendFirstExamCompletedEmail');
        await sendFirstExamEmail({
          uid: userId,
          email: user.email,
          displayName: user.displayName || null,
          examProvider: examData.examProvider || 'Exam',
          examLevel: examData.level || '',
          resultsUrl: 'https://logami.app/exam/results',
        });
      } catch (emailErr) {
        console.warn('First exam email (optional) failed:', emailErr);
      }
      return saveResult.id; // Return the ID
    }

    return null;
  } catch (error) {
    console.error('Error logging exam completion:', error);
    return null;
  }
};
// ... (other functions remain the same)
export const logLessonCompletion = async (lessonData) => {
  const user = auth.currentUser;
  if (!user) return;

  trackFirstStudyAction({ userId: user.uid, actionType: 'lesson', activityId: lessonData.lessonId });

  try {
    console.log('Logging lesson completion:', lessonData.title);

    // Track the session first
    await enhancedUserService.trackStudySession({
      type: 'lesson',
      activityId: lessonData.lessonId,
      title: lessonData.title,
      duration: lessonData.timeSpent || 0,
      score: lessonData.score,
      maxScore: lessonData.maxScore,
      completed: true,
      correctAnswers: lessonData.correctAnswers,
      totalAnswers: lessonData.totalQuestions,
      skillsWorked: lessonData.skills || ['general'],
      topicsStudied: lessonData.topics || [],
      difficultyLevel: lessonData.level || 'beginner'
    });

    // THEN check for achievement notifications
    const prefs = await notificationService.loadPreferences(user.uid);

    if (prefs.enabled && prefs.achievementAlerts) {
      // Get user's actual completed lesson count from their profile
      const userProfile = await enhancedUserService.getUserProfile(user.uid);
      const totalLessons = userProfile?.stats?.completedLessons || 0;

      // Check milestones
      if (totalLessons === 50) {
        await notificationService.sendAchievementNotification('Grammar Novice - 50 Lessons!');
      } else if (totalLessons === 100) {
        await notificationService.sendAchievementNotification('Grammar Master - 100 Lessons!');
      }
    }

    console.log('Lesson completion logged successfully');
  } catch (error) {
    console.error('Error logging lesson completion:', error);
  }
};

export const checkDailyReminder = async (userId) => {
  const prefs = await notificationService.loadPreferences(userId);

  if (!prefs.enabled || !prefs.studyReminders) return;

  const now = new Date();
  const currentTime = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;

  if (currentTime === prefs.dailyReminderTime) {
    await notificationService.sendDailyReminder();
  }
};
export const logVocabularyLearning = async (words) => {
  const user = auth.currentUser;
  if (!user) return;

  trackFirstStudyAction({ userId: user.uid, actionType: 'vocabulary', activityId: 'vocab_practice' });

  try {
    console.log('Logging vocabulary learning:', words.length, 'words');

    for (const word of words) {
      await enhancedUserService.trackVocabulary({
        word: word.german || word.word,
        translation: word.english || word.translation,
        definition: word.definition,
        example: word.example,
        difficulty: word.difficulty || 'beginner',
        category: word.category || 'general'
      });
    }

    // Also track as study session
    await enhancedUserService.trackStudySession({
      type: 'vocabulary',
      activityId: 'vocab_practice',
      title: 'Vocabulary Practice',
      duration: words.length * 2,
      completed: true,
      vocabularyLearned: words,
      skillsWorked: ['vocabulary']
    });

    console.log('Vocabulary learning logged successfully');
  } catch (error) {
    console.error('Error logging vocabulary learning:', error);
  }
};
export const logPracticeSession = async (practiceData) => {
  const user = auth.currentUser;
  if (!user) return;

  trackFirstStudyAction({ userId: user.uid, actionType: 'practice', activityId: practiceData.sectionId });

  try {
    console.log('Logging practice session:', practiceData.sectionName);

    await enhancedUserService.trackStudySession({
      type: 'practice',
      activityId: practiceData.sectionId,
      title: `Practice: ${practiceData.sectionName}`,
      duration: practiceData.timeSpent || 0,
      score: practiceData.correctAnswers,
      maxScore: practiceData.totalQuestions,
      completed: true,
      correctAnswers: practiceData.correctAnswers,
      totalAnswers: practiceData.totalQuestions,
      skillsWorked: [practiceData.sectionId],
      difficultyLevel: practiceData.level || 'beginner'
    });

    console.log('Practice session logged successfully');

  } catch (error) {
    console.error('Error logging practice session:', error);
  }
};

export const logGrammarPractice = async (grammarData) => {
  const user = auth.currentUser;
  if (!user) return;

  trackFirstStudyAction({ userId: user.uid, actionType: 'grammar', activityId: grammarData.topicId });

  try {
    console.log(`Logging ${grammarData.level || 'B1'} grammar practice:`, grammarData.topicName);

    // Enhanced grammar data with level awareness
    const enhancedGrammarData = {
      type: 'grammar',
      activityId: `grammar_${grammarData.level?.toLowerCase() || 'b1'}_${grammarData.topicId}`,
      title: `${grammarData.level || 'B1'} Grammar: ${grammarData.topicName}`,
      duration: grammarData.timeSpent || 0,
      score: grammarData.correctAnswers,
      maxScore: grammarData.totalQuestions,
      completed: true,
      correctAnswers: grammarData.correctAnswers,
      totalAnswers: grammarData.totalQuestions,
      skillsWorked: ['grammar'],
      topicsStudied: [grammarData.topicName],
      difficultyLevel: grammarData.level?.toLowerCase() || 'intermediate',

      // Level-specific metadata
      grammarLevel: grammarData.level || 'B1',
      grammarPillar: grammarData.topicId,
      exerciseType: grammarData.exerciseType || 'mixed',
      exerciseDifficulty: grammarData.difficulty || 'medium',

      // Enhanced analytics data
      metadata: {
        isLevelSpecific: true,
        grammarSystem: 'pillar-based',
        version: '2.0',
        levelMigration: grammarData.level !== 'B1' ? 'multi-level' : 'legacy'
      }
    };

    await enhancedUserService.trackStudySession(enhancedGrammarData);

    console.log(`${grammarData.level || 'B1'} grammar practice logged successfully`);
  } catch (error) {
    console.error(`Error logging ${grammarData.level || 'B1'} grammar practice:`, error);
  }
};

export const logGrammarPillarProgress = async (pillarData) => {
  try {
    console.log(`Logging ${pillarData.level} pillar progress:`, pillarData.pillarName);

    const pillarProgressData = {
      type: 'grammar_pillar',
      activityId: `pillar_${pillarData.level?.toLowerCase()}_${pillarData.pillarId}`,
      title: `${pillarData.level} Pillar: ${pillarData.pillarName}`,
      duration: pillarData.timeSpent || 0,
      score: pillarData.exercisesCompleted,
      maxScore: pillarData.totalExercises,
      completed: pillarData.pillarCompleted || false,
      skillsWorked: ['grammar'],
      topicsStudied: [pillarData.pillarName],
      difficultyLevel: pillarData.level?.toLowerCase() || 'intermediate',

      // Pillar-specific data
      grammarLevel: pillarData.level,
      pillarId: pillarData.pillarId,
      pillarProgress: pillarData.progressPercentage || 0,
      exercisesCompleted: pillarData.exercisesCompleted || 0,
      totalExercises: pillarData.totalExercises || 0,

      metadata: {
        isPillarCompletion: pillarData.pillarCompleted || false,
        pillarCategory: pillarData.pillarCategory || 'grammar',
        studySession: 'pillar-based-learning'
      }
    };

    await enhancedUserService.trackStudySession(pillarProgressData);

    console.log(`${pillarData.level} pillar progress logged successfully`);
  } catch (error) {
    console.error(`Error logging ${pillarData.level} pillar progress:`, error);
  }
};

export const logWritingExercise = async (writingData) => {
  try {
    console.log('Logging writing exercise:', writingData.title);

    await enhancedUserService.trackStudySession({
      type: 'writing',
      activityId: writingData.exerciseId,
      title: `Writing: ${writingData.title}`,
      duration: writingData.timeSpent || 0,
      score: writingData.score,
      maxScore: writingData.maxScore,
      completed: true,
      skillsWorked: ['writing'],
      topicsStudied: writingData.topics || [],
      difficultyLevel: writingData.level || 'beginner'
    });
    console.log('Writing exercise logged successfully');
  } catch (error) {
    console.error('Error logging writing exercise:', error);
  }
};
export const logListeningExercise = async (listeningData) => {
  try {
    console.log('Logging listening exercise:', listeningData.title);

    await enhancedUserService.trackStudySession({
      type: 'listening',
      activityId: listeningData.exerciseId,
      title: `Listening: ${listeningData.title}`,
      duration: listeningData.timeSpent || 0,
      score: listeningData.correctAnswers,
      maxScore: listeningData.totalQuestions,
      completed: true,
      correctAnswers: listeningData.correctAnswers,
      totalAnswers: listeningData.totalQuestions,
      skillsWorked: ['listening'],
      topicsStudied: listeningData.topics || [],
      difficultyLevel: listeningData.level || 'beginner'
    });

    console.log('Listening exercise logged successfully');
  } catch (error) {
    console.error('Error logging listening exercise:', error);
  }
};
export const logReadingExercise = async (readingData) => {
  try {
    console.log('Logging reading exercise:', readingData.title);

    await enhancedUserService.trackStudySession({
      type: 'reading',
      activityId: readingData.exerciseId,
      title: `Reading: ${readingData.title}`,
      duration: readingData.timeSpent || 0,
      score: readingData.correctAnswers,
      maxScore: readingData.totalQuestions,
      completed: true,
      correctAnswers: readingData.correctAnswers,
      totalAnswers: readingData.totalQuestions,
      skillsWorked: ['reading'],
      topicsStudied: readingData.topics || [],
      difficultyLevel: readingData.level || 'beginner'
    });

    console.log('Reading exercise logged successfully');
  } catch (error) {
    console.error('Error logging reading exercise:', error);
  }
};
export const logSpeakingExercise = async (speakingData) => {
  try {
    console.log('Logging speaking exercise:', speakingData.title);

    await enhancedUserService.trackStudySession({
      type: 'speaking',
      activityId: speakingData.exerciseId,
      title: `Speaking: ${speakingData.title}`,
      duration: speakingData.timeSpent || 0,
      score: speakingData.score,
      maxScore: speakingData.maxScore,
      completed: true,
      skillsWorked: ['speaking'],
      topicsStudied: speakingData.topics || [],
      difficultyLevel: speakingData.level || 'beginner'
    });

    console.log('Speaking exercise logged successfully');
  } catch (error) {
    console.error('Error logging speaking exercise:', error);
  }
};
export const logStudyPlanActivity = async (activityData) => {
  try {
    console.log('Logging study plan activity:', activityData.activityName);

    await enhancedUserService.trackStudySession({
      type: activityData.activityType || 'study_plan',
      activityId: activityData.activityId,
      title: `Study Plan: ${activityData.activityName}`,
      duration: activityData.timeSpent || 0,
      score: activityData.score,
      maxScore: activityData.maxScore,
      completed: activityData.completed || true,
      correctAnswers: activityData.correctAnswers,
      totalAnswers: activityData.totalQuestions,
      skillsWorked: activityData.skills || [],
      topicsStudied: activityData.topics || [],
      difficultyLevel: activityData.level || 'beginner'
    });

    console.log('Study plan activity logged successfully');
  } catch (error) {
    console.error('Error logging study plan activity:', error);
  }
};
export const logDailyLogin = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('No user logged in, skipping daily login');
      return 0;
    }

    // Just check/update streak without logging a session
    console.log('Checking daily streak...');
    const updatedStreak = await enhancedUserService.updateStudyStreak(user.uid);

    console.log(`Daily streak check complete.`);
    return updatedStreak;
  } catch (error) {
    console.error('Error processing daily login:', error);
    return 0;
  }
};

export const exportUserData = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;

    const result = await enhancedUserService.exportUserData(userId);
    if (result.success) {
      // Create downloadable file
      const dataStr = JSON.stringify(result.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `logami-user-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('User data exported successfully');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error exporting user data:', error);
    return false;
  }
};
export const logRedemittelLearning = async (phrases) => {
  try {
    await logActivity({
      type: 'redemittel',
      subType: 'practice',
      details: {
        phrasesLearned: phrases.length,
        phrases: phrases.map(phrase => ({
          phrase: phrase.phrase,
          context: phrase.context,
          category: phrase.category
        })),
        level: phrases[0]?.level || 'B1',
        category: phrases[0]?.category || 'general'
      },
      xpEarned: phrases.length * 5, // 5 XP per phrase
      timeSpent: phrases.length * 2 // Estimate 2 minutes per phrase
    });
  } catch (error) {
    console.error('Error logging redemittel learning:', error);
    throw error;
  }
};